import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import * as Icons from "lucide-react";
import { StatTicker } from "../components/StatTicker";
import { BrazilBlueprint } from "../components/BrazilBlueprint";
import { People } from "../components/People";
import { useEntity } from "../lib/entity";

export default function About() {
  const [settings, setSettings] = useState({});
  const [team, setTeam] = useState([]);
  const [services, setServices] = useState([]);
  const entity = useEntity();

  useSeo({
    title: "About us — KickStartKenyaFootball",
    description: "Who we are, why we exist and the people building Kenya's football future.",
  });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/team").then((r) => setTeam(r.data));
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  return (
    <div className="bg-black" data-testid="about-page">
      <section className="px-5 pb-14 pt-12 md:px-10 md:pt-16">
        <div className="mx-auto max-w-[1500px]">
          <p className="eyebrow">About us</p>
          <h1 className="display-1 mt-5 max-w-4xl">Kenya Has Always Had The Talent</h1>
          <div className="mt-12 grid gap-12 lg:grid-cols-2">
            <p className="serif text-xl italic leading-relaxed text-zinc-300 md:text-2xl">
              Kenya produces top footballers despite minimum investment into football infrastructure. With moderate
              investment into football infrastructure Kenya can produce more top players — and a sector that pays wages,
              taxes and tourism receipts back into the country.
            </p>
            <div className="space-y-6 text-base leading-relaxed text-zinc-300">
              <p>{settings.intro_body}</p>
              <p>
                The journey runs across recommended steps: planning and rebranding, stackable pitches and futsal
                courts, county academies and coach education, and modular community stadiums in key counties.
                Each carries an indicative cost estimate rather than a committed budget, and each is open to
                better ideas from people closer to the ground.
              </p>
              <p>
                That direction is deliberately aligned with the Kenya Premier League Strategic Plan 2022-2027 — its
                grassroots and schools programme, its football business academy, its work on club structures and
                corporate governance, and its push to establish clubs and facilities outside Nairobi, where roughly 87%
                of Kenyans live.
              </p>
              <p>
                We publish every target, every scoring model and every estimate so we can be argued with — and held to
                account. If you can deliver part of it better, faster or cheaper,{" "}
                <Link to="/apply" className="text-ochre underline decoration-maasai underline-offset-4">
                  send us a proposal
                </Link>
                .
              </p>
            </div>
          </div>
          <div className="mt-16 grid grid-cols-2 gap-8 md:grid-cols-4">
            {(settings.stats || []).map((s, i) => (
              <StatTicker key={s.label} value={s.value} label={s.label} delay={i * 0.08} />
            ))}
          </div>
        </div>
      </section>

      <section id="team" className="border-t border-zinc-900 bg-[#0b0b0b] py-20 md:py-28">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <People group={entity?.people?.trustees} />
          <People group={entity?.people?.kenya} />

          <p className="eyebrow mt-20">Open positions</p>
          <h2 className="display-2 mt-5 max-w-3xl">Every other position is currently vacant</h2>
          <p className="body-copy mt-6 max-w-2xl">
            Beyond the trustee, these are the roles the organisation needs to run properly. All of them are open,
            unfilled and unpaid until funding is in place — no appointments have been made. If you have the
            experience for one of them, or you can fund it, tell us.
          </p>
          <div className="mt-12 border-t border-white/20" data-testid="about-team-grid">
            {team.map((m, i) => (
              <div
                key={m.id}
                className="grid grid-cols-[46px_1fr_auto] items-center gap-4 border-b border-white/10 py-5 md:grid-cols-[64px_minmax(0,2fr)_auto] md:gap-8"
                data-testid={`about-role-${m.slug}`}
              >
                <span className="mono text-sm text-maasai">{String(i + 1).padStart(2, "0")}</span>
                <span className="min-w-0">
                  <span className="block font-display text-lg uppercase leading-tight tracking-tight text-white md:text-2xl">
                    {m.role || m.name}
                  </span>
                  {m.bio ? <span className="mt-1.5 block text-sm text-zinc-500">{m.bio}</span> : null}
                </span>
                <span
                  className="justify-self-end bg-maasai px-3 py-1.5 text-[0.58rem] font-bold uppercase tracking-[0.2em] text-white"
                  data-testid={`about-role-status-${m.slug}`}
                >
                  {m.status || "Vacant"}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-20">
            <p className="eyebrow">Focus areas</p>
            <h2 className="display-2 mt-4 max-w-2xl">On and off the pitch</h2>
            <div className="mt-10 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((s) => {
                const Icon = Icons[s.icon] || Icons.Users;
                return (
                  <div key={s.id} className="group bg-[#0b0b0b] p-8 transition-colors hover:bg-[#141414]" data-testid={`service-card-${s.slug}`}>
                    <Icon size={24} strokeWidth={1.4} className="text-maasai transition-transform duration-500 group-hover:scale-110" />
                    <h3 className="mt-5 font-display text-lg font-semibold uppercase tracking-tight text-white">{s.title}</h3>
                    <p className="body-copy mt-3">{s.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-20">
            <p className="eyebrow">Global footprint</p>
            <h2 className="display-2 mt-4">Nairobi to the world</h2>
            <ul className="mt-8 divide-y divide-zinc-900 border-t border-zinc-900" data-testid="about-hubs">
              {(settings.hubs || []).map((h) => (
                <li
                  key={h.city}
                  className="flex items-baseline justify-between py-4"
                  data-testid={`hub-${h.city.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="font-display text-lg uppercase tracking-wide text-white">{h.city}</span>
                  <span className="text-[0.7rem] uppercase tracking-[0.16em] text-zinc-500">{h.role}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-20">
            <BrazilBlueprint />
          </div>

          <div className="mt-16 flex flex-wrap gap-4">
            <Link to="/plans" className="btn-primary">See the recommended steps</Link>
            <Link to="/apply" className="btn-ghost">Send Us Your Research</Link>
            <Link to="/contact" className="btn-ghost">Talk to us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
